import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const Modal = ({ isOpen, onClose, title, children, size = 'md', footer }) => {
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        full: 'max-w-[min(1440px,96vw)]',
    };
    // Large settings modals get a fixed height so the body scrolls and the footer stays on screen.
    const heightClass = size === 'full' ? 'h-[calc(100dvh-2rem)]' : 'max-h-[calc(100dvh-2rem)]';

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose?.();
            }
        };

        // Lock page scroll behind the modal.
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // Portal to <body> so no ancestor (transform, filter, overflow) can reposition or clip the overlay.
    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div
                className={`w-full ${sizeClasses[size]} bg-white rounded-2xl shadow-xl border border-slate-200 ${heightClass} overflow-hidden flex flex-col`}
            >
                <div className="flex flex-shrink-0 items-center justify-between px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
                {footer && <div className="flex-shrink-0 border-t border-slate-200 bg-white px-6 py-4">{footer}</div>}
            </div>
        </div>,
        document.body
    );
};

export default Modal;
